/**
 * HiredInBanner — surfaces gear on this event that is not owned stock and must
 * source from a supplier (hired-in gear, PR2).
 *
 * Informational only (blue, no action): the operator opens the event and
 * immediately sees what needs sourcing and who can supply it — so they can do
 * it without contacting the owner, which is the whole point of the transition.
 *
 * Reads straight off the workspace gear lines: any line with is_hired_in=true
 * is listed, with each linked supplier's name and contact (phone/email) so the
 * operator can call to source it without leaving the page. Renders nothing when
 * no line is hired-in.
 *
 * Dismissal is ephemeral (DESIGN.md category 2): this is a read-once notice —
 * the operator sees what needs sourcing, closes it, and works the booking. It
 * returns on reload because the next visit is a fresh work session.
 *
 * The dismissal is keyed on the hired-in set, not a bare boolean: adding
 * another hired-in line after a dismiss must re-show the banner, or a closed
 * banner would silently hide an item that was never seen.
 */
import { useState } from "react";
import { Banner } from "../ui/Banner";
import styles from "./HiredInBanner.module.css";

interface HiredInSupplier {
  name: string;
  email?: string | null;
  phone?: string | null;
}

interface HiredInGearLine {
  item_name: string;
  is_hired_in?: boolean;
  suppliers?: HiredInSupplier[];
}

interface HiredInBannerProps {
  gearItems: HiredInGearLine[];
}

export function HiredInBanner({ gearItems }: HiredInBannerProps) {
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  const hiredIn = gearItems.filter((g) => g.is_hired_in);
  if (hiredIn.length === 0) return null;

  const signature = hiredIn.map((g) => g.item_name).join(" ");
  if (dismissedFor === signature) return null;

  const count = hiredIn.length;

  return (
    <Banner
      variant="info"
      className={styles.banner}
      data-testid="hired-in-banner"
      onDismiss={() => setDismissedFor(signature)}
      dismissLabel="Dismiss sourcing notice"
      title={`${count} item${count === 1 ? "" : "s"} need${count === 1 ? "s" : ""} sourcing`}
    >
      <ul className={styles.list}>
        {hiredIn.map((g, i) => {
          const suppliers = (g.suppliers ?? []).filter((s) => s && s.name);
          return (
            <li key={`${g.item_name}-${i}`}>
              <span className={styles.itemName}>{g.item_name}</span>
              {suppliers.length > 0 ? (
                <ul className={styles.suppliers}>
                  {suppliers.map((s, j) => {
                    const contact = [s.phone, s.email].filter(
                      Boolean,
                    ) as string[];
                    return (
                      <li key={`${s.name}-${j}`} className={styles.supplier}>
                        {s.name}
                        {contact.length > 0 ? (
                          <span className={styles.contact}>
                            : {contact.join(" · ")}
                          </span>
                        ) : (
                          <span className={styles.missing}>
                            , no contact on file
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <span className={styles.missing}> (no supplier linked)</span>
              )}
            </li>
          );
        })}
      </ul>
    </Banner>
  );
}
